<template>
  <div class="grid grid-cols-12">
    <div class="col-span-3 space-y-12 bg-transaction-gradient
                p-8 pt-16 lg:p-12 lg:pl-14">
      <ValueInput v-model.trim="$v.form.sum.$model"
                  :label="$t('sum')"
                  :currency_id="!!form.account ? form.account.currency.id : null"
                  :class="{'--error':$v.form.sum.$error}">
        <template v-slot:errors="">
          <div class="error" v-if="$v.form.sum.$invalid">{{ $t('fieldRequired') }}</div>
        </template>
      </ValueInput>
      <ValueInput v-model="$v.form.fee.$model"
                  v-if="!!form.account && !!form.account.commission && (template.type === 2)"
                  :label="$t('commission')"
                  :currency_id="!!form.account ? form.account.currency.id : null">
      </ValueInput>
    </div>
    <div class="col-span-9
                p-8 pt-16 lg:p-12 lg:pr-17
                grid grid-cols-2 col-gap-12 row-gap-12">
      <AccountsSelect v-model="form.account"
                      :label="template.type === 1 ? $t('accountTo') : $t('accountFrom')"
                      :accounts="accounts.data"
                      :account_groups="accounts_by_groups"
                      :grouped="accounts_by_groups.length > 1"
                      @open="open_form = true"/>
      <BaseSelect v-model="form.incomeCategory"
                  v-if="template.type === 1"
                  :options="incomes"
                  :class="{'--error':$v.form.incomeCategory.$error}"
                  :allow-empty="false"
                  :selectLabel="$t('categoryIncome')"
                  class="col-span-1"
                  track-by="id"
                  label="title">
        <template v-slot:errors>
          <div class="error" v-if="$v.form.incomeCategory.$invalid">{{ $t('fieldRequired') }}</div>
        </template>
      </BaseSelect>
      <BaseSelect v-model="form.expenseCategory"
                  v-else-if="template.type === 2"
                  :options="expenses_by_groups"
                  class="col-span-1"
                  :class="{'--error':$v.form.expenseCategory.$error}"
                  :allow-empty="false"
                  :group-select="false"
                  :searchable="true"
                  :show-labels="false"
                  :select-label="$t('categoryExpense')"
                  track-by="id"
                  label="title"
                  group-label="title"
                  group-values="childs">
        <template v-slot:errors>
          <div class="error" v-if="$v.form.expenseCategory.$invalid">{{ $t('fieldRequired') }}</div>
        </template>
      </BaseSelect>
      <BaseSelect v-if="options['agent'] && $store.getters['space/spaceUseContractors']"
                  class="col-span-1"
                  v-model="form.contractor"
                  :options="computedContractors"
                  :allow-empty="true"
                  :show-labels="false"
                  :select-label="$t('contractor')"
                  track-by="id"
                  label="title"
                  placeholder="Выберите ...">
        <template v-slot:noOptions="">
          <div class="w-full whitespace-normal">
            {{ $t('noContractors') }}
            <router-link :to="{name:'settings-contractors'}" class="text-red">
              {{ $t('noContractorsHere') }}
            </router-link>
          </div>
        </template>
      </BaseSelect>
      <BaseTextarea v-if="options['comment']"
                    class="col-span-2"
                    v-model="form.comment"
                    :label="$t('comment')"/>
      <BaseSelect v-if="options['tags']"
                  class="col-span-1"
                  v-model="form.tags"
                  :options="tags_list"
                  :close-on-select="false"
                  :multiple="true"
                  :taggable="true"
                  :show-labels="false"
                  :selectLabel="$t('tags')"
                  :tag-placeholder="$t('tagCreateNew')"
                  track-by="id"
                  label="title"
                  @tag="addTag">
        <template v-slot:noOptions>
          <div class="w-full whitespace-normal">
            {{ $t('noTags') }}
          </div>
        </template>
      </BaseSelect>
      <BaseSelect v-model="form.repeat_type"
                  :selectLabel="$t('repeatLabel')"
                  label="name"
                  :options="repeatOptions"
                  :searchable="true"
                  :show-labels="false"/>
      <VueCtkDateTimePicker v-model="form.date"
                            id="transaction-date"
                            format="YYYY-MM-DD"
                            formatted="DD.MM.YYYY"
                            :locale="$i18n.locale"
                            :label="$t('dateLabel')"
                            :auto-close="true"
                            :only-date="true"
                            :no-clear-button="true"
                            :no-button="true"
                            color="#2D4C5E"/>
      <div class="col-span-2 grid grid-cols-2 col-gap-12  -mt-5">
        <BaseCheckbox :checked="form.off_report" size="sm"
                      @change="form.off_report = !form.off_report">
          <span class="text-sm">{{ $t('isReportText') }}</span>
        </BaseCheckbox>
        <BaseCheckbox :checked="form.off_budget" size="sm"
                      @change="form.off_budget = !form.off_budget">
          <span class="text-sm">{{ $t('isBudgetText') }}</span>
        </BaseCheckbox>
      </div>

      <div class="col-span-2 flex items-center">
        <BaseButton @click="saveChanges"
                    class="mr-8"
                    style="width: 255px;"
                    :loading="submitted"
                    theme="gray">
          {{ $t('saveChanges') }}
        </BaseButton>
        <BaseButton @click="$emit('cancel')"
                    style="width: 140px;"
                    :loading="submitted"
                    theme="white">
          {{ $t('cancel') }}
        </BaseButton>
        <ArchiveBtn class="ml-auto"
                    @confirm="removeOperation"
                    :submitted="submitted">
          <template v-slot:text>
            {{ $t('remove') }}
          </template>
        </ArchiveBtn>
      </div>
      <div class="text-sm text-text-ternary col-span-2 -my-6">
        {{ $t('editingTemplateTip') }}
      </div>
    </div>
  </div>
</template>

<script>
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import AccountsSelect from "@/components/UI/BaseSelect/AccountsSelect";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseButton from "@/components/UI/BaseButton";
import BaseCheckbox from "@/components/UI/BaseCheckbox";
import ArchiveBtn from "@/components/UI/ArchiveBtn";
import {decimal, required, requiredIf} from "vuelidate/lib/validators";
import {mapActions, mapGetters, mapState} from "vuex";
import BaseTextarea from "@/components/UI/BaseTextarea";

export default {
  name: "PlannedTransactionEditForm",
  components: {BaseTextarea, ArchiveBtn, BaseCheckbox, BaseButton, BaseSelect, AccountsSelect, ValueInput},
  props: {
    template: {
      required: true
    },
    options: {
      required: true
    },
    submitted: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      form: {
        sum: null,
        fee: null,
        account: null,
        expenseCategory: null,
        incomeCategory: null,
        contractor: null,
        comment: '',
        tags: [],
        date: null,
        off_report: false,
        off_budget: false,
        repeat_type: null,
      }
    }
  },
  validations: {
    form: {
      sum: {
        required,
        decimal,
      },
      fee: {
        decimal,
      },
      expenseCategory: {
        required: requiredIf(function () {
          return this.template.type === 2
        }),
      },
      incomeCategory: {
        required: requiredIf(function () {
          return this.template.type === 1
        }),
      },
    }
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('space/expenses', ['expenses']),
    ...mapState('space/incomes', ['incomes']),
    ...mapState('space/tags', ['tags_list']),
    ...mapState('space/accounts', ['accounts']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    ...mapGetters('space/expenses', ['expenses_by_groups']),
    ...mapGetters('space/contractors', ['contractorsByPopularity']),
    ...mapGetters('cabinet/constants', ['getConstantList']),
    computedContractors() {
      return this.contractorsByPopularity(this.template.type)
    },
    repeatOptions() {
      return this.getConstantList('calendar_repeat')
    }
  },
  methods: {
    ...mapActions('space/tags', ['createTag']),
    addTag(val) {
      const tag = {title: val, space_id: this.current_space.data.id};
      this.createTag(tag).then(res => {
        if (res.body.success) {
          this.form.tags.push(res.body.data);
          this.$store.commit('space/tags/pushTag', res.body.data);
        }
      })
    },
    handleFee() {
      const {account, sum} = this.form;

      if (!!account && account.commission && !!sum) {
        if (account.commission_type === 1) {
          const s = sum * 100,
              f = +account.commission_value;
          this.form.fee = +(s * f / 10000).toFixed(account.currency.decimals);
        } else {
          this.form.fee = +account.commission_value;
        }
      } else {
        this.form.fee = null;
      }
    },
    setFields(template) {
      this.form.sum = !!template.commission ? template.sum - template.commission : template.sum;
      this.form.sum = this.form.sum.toFixed(template.account.currency.decimals)
      this.form.fee = !!template.commission ? template.commission : 0;
      this.form.account = template.account;
      this.form.expenseCategory = template.expense;
      this.form.incomeCategory = template.income;
      this.form.contractor = template.contractor;
      this.form.comment = !!template.comment ? template.comment : '';
      this.form.tags = template.tags;

      this.form.date = template.next_event_at;
      this.form.repeat_type = this.repeatOptions.find((item) => item.id === template.interval)
      this.form.off_report = template.off_report;
      this.form.off_budget = template.off_budget;
    },
    prepareData(payload) {
      let data = {
        id: this.template.id,
        account_id: payload.account.id,
        date: payload.date,
        comment: this.options.comment ? payload.comment : '',
        tag_ids: this.options.tags ? payload.tags.map(i => i.id) : [],
        contractor_id: (this.options.agent && !!payload.contractor) ? payload.contractor.id : null,
        off_report: this.form.off_report,
        off_budget: this.form.off_budget,
        repeat_type: this.form.repeat_type.id
      }

      if (this.template.type === 2) {
        data.sum = payload.account.commission_type !== null ? (+payload.sum + +payload.fee) : +payload.sum;
        data.expense_category_id = !!payload.expenseCategory ? payload.expenseCategory.id : '';
        data.commission = payload.account.commission_type !== null ? +payload.fee : null;
      }
      if (this.template.type === 1) {
        data.sum = +payload.sum;
        data.income_source_id = !!payload.incomeCategory ? payload.incomeCategory.id : '';
      }

      return data;
    },
    saveChanges() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.$emit('updateTemplate', this.prepareData(this.form))
      }
    },
    removeOperation() {
      this.$emit('removeTemplate', this.template.id)
    }
  },
  watch: {
    template: {
      immediate: true,
      handler(newVal) {
        if (!!newVal) {
          this.setFields(newVal)
        }
      }
    },
    'form.sum': {
      handler() {
        this.handleFee();
      }
    },
    'form.account': {
      handler() {
        this.handleFee()
      }
    }
  },
  i18n: {
    messages: {
      en: {
        fieldRequired: 'Field is required',
        sum: 'Sum',
        commission: 'Commission',
        accountFrom: 'From account',
        accountTo: 'To account',
        categoryExpense: 'Expense category',
        categoryIncome: 'Income category',
        contractor: 'Contractor',
        noContractors: 'You have no contractors, you can add them',
        noContractorsHere: 'here',
        comment: 'Comment',
        tags: 'Tags',
        tagCreateNew: 'Create new',
        noTags: 'You have no tags, enter name to create the new tag',
        repeatLabel: 'Repeat',
        isReportText: 'ignore in reports',
        isBudgetText: 'ignore in budgets',
        dateLabel: 'Transaction date',
        timeLabel: 'Transaction time',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        remove: 'Delete',
        editingTemplateTip: 'You are editing a transaction template, the changes will affect all future and unconfirmed scheduled transactions'
      },
      ru: {
        fieldRequired: 'Поле обязательно',
        sum: 'Сумма',
        commission: 'Комиссия',
        accountFrom: 'Со счета',
        accountTo: 'На счет',
        categoryExpense: 'Категория расхода',
        categoryIncome: 'Категория дохода',
        contractor: 'Контрагент',
        noContractors: 'У вас пока нет контрагентов, вы можете добавить их',
        noContractorsHere: 'здесь',
        comment: 'Комментарий',
        tags: 'Теги',
        tagCreateNew: 'Добавить новый',
        noTags: 'У вас пока нет тегов, введите названия для нового тега',
        repeatLabel: 'Повторять',
        isReportText: 'не учитывать сумму в отчетах',
        isBudgetText: 'не учитывать сумму в бюджетах',
        dateLabel: 'Дата операции',
        timeLabel: 'Время операции',
        saveChanges: 'Сохранить изменения',
        cancel: 'Отмена',
        remove: 'Удалить',
        editingTemplateTip: 'Вы редактируете шаблон транзакции, изменения повлияют на все будущие и неподтвержденные запланированные операции'
      }
    }
  }
}
</script>

<style scoped>

</style>